import { useState, useEffect, useCallback, useRef } from 'react'

interface UsePollingOptions<T> {
  fetcher: () => Promise<T>
  interval?: number
  enabled?: boolean
}

export function usePolling<T>({ fetcher, interval = 5000, enabled = true }: UsePollingOptions<T>) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const mountedRef = useRef(true)
  const fetcherRef = useRef(fetcher)
  const inFlightRef = useRef(false)

  // Keep fetcher ref updated without triggering effect re-runs
  useEffect(() => {
    fetcherRef.current = fetcher
  }, [fetcher])

  const fetchData = useCallback(async () => {
    // Skip if already fetching to prevent request pile-up
    if (inFlightRef.current) return

    inFlightRef.current = true
    try {
      const result = await fetcherRef.current()
      if (mountedRef.current) {
        setData(result)
        setError(null)
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error('Failed to fetch data'))
      }
    } finally {
      inFlightRef.current = false
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true

    if (!enabled) {
      setLoading(false)
      return
    }

    // Initial fetch
    fetchData()

    // Set up polling
    const pollInterval = setInterval(fetchData, interval)

    return () => {
      mountedRef.current = false
      clearInterval(pollInterval)
    }
  }, [fetchData, interval, enabled])

  const refetch = useCallback(() => {
    setLoading(true)
    inFlightRef.current = false // Allow manual refetch even if one is in-flight
    return fetchData()
  }, [fetchData])

  return { data, loading, error, refetch }
}
